import React from "react";
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { visualizationData } from "./data/data";
import { calculateTotalTests } from "./utils";
import { AgentLayout } from "./AgentLayout";

// Helper function to find a node by ID recursively
const findNodeById = (data: any, id: string): any => {
  if (data.id === id) {
    return data;
  }
  if (data.children) {
    for (const child of data.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
};

// Component to render test as a clickable list item
const TestListItem: React.FC<{ test: any; index: number; agentId: string }> = ({
  test,
  index,
  agentId,
}) => {
  const navigate = useNavigate();

  return (
    <div
      className="flex items-center justify-between p-4 bg-gray-750 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
      onClick={() => navigate({ to: `/test-runs/${agentId}/test/${test.id}` })}
    >
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          {test.success ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500" />
          )}
          <span className="text-sm font-medium text-gray-200">
            {test.input}
          </span>
        </div>
      </div>

      <div className="flex items-center space-x-4 text-sm text-gray-400">
        <span className={test.success ? "text-green-400" : "text-red-400"}>
          {test.success ? "Passed" : "Failed"}
        </span>
      </div>
    </div>
  );
};

export const TopicDetailsPage: React.FC = () => {
  const { agentId, topicId } = useParams({
    from: "/ai-agents/$agentId/topics/$topicId",
  });
  const navigate = useNavigate();

  // Find the topic node by ID
  const topicNode = findNodeById(visualizationData, topicId);

  if (!topicNode) {
    return (
      <AgentLayout activeTab="topics">
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-100 mb-4">
              Topic Not Found
            </h1>
            <p className="text-gray-400 mb-4">
              The requested topic could not be found.
            </p>
            <Link
              to="/ai-agents/$agentId"
              params={{ agentId }}
              className="inline-flex items-center text-blue-400 hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Agent
            </Link>
          </div>
        </div>
      </AgentLayout>
    );
  }

  // Calculate totals and pass rate only if topicNode exists
  const totals = calculateTotalTests(topicNode);
  const passRate = totals.total > 0 ? (totals.passed / totals.total) * 100 : 0;

  return (
    <AgentLayout activeTab="topics">
      <div className="space-y-6">
        {/* Topic Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {passRate >= 80 ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : passRate >= 50 ? (
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <h1 className="text-xl font-bold text-gray-100">
                {topicNode.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl font-bold text-gray-100">
                Coverage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-100">
                {topicNode.testCoverage}%
              </div>
              <Progress value={topicNode.testCoverage} className="mt-2" />
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-200">
                Pass Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-100">
                {passRate.toFixed(1)}%
              </div>
              <Progress value={passRate} className="mt-2" />
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-200">
                Total Tests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-100">
                {totals.total}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {totals.passed} passed, {totals.failed} failed
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-200">
                Sub-topics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-100">
                {topicNode.children ? topicNode.children.length : 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recommendation */}
        {topicNode.recommendation && (
          <div className="rounded-xl bg-transparent border border-pink-400/50 shadow-[0_0_20px_rgba(236,72,153,0.3),inset_0_0_20px_rgba(236,72,153,0.1)] p-6">
            <h3 className="text-lg font-semibold text-white mb-3">
              Rippletide recommendation
            </h3>
            <p className="text-gray-200 leading-relaxed">
              {topicNode.recommendation}
            </p>
          </div>
        )}

        {/* Tests List */}
        {topicNode.tests && topicNode.tests.length > 0 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-gray-100">Tests</CardTitle>
              <CardDescription className="text-gray-400">
                Individual test results for this topic
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topicNode.tests.map((test: any, index: number) => (
                  <TestListItem
                    key={test.id}
                    test={test}
                    index={index}
                    agentId={agentId}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sub-topics List */}
        {topicNode.children && topicNode.children.length > 0 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-gray-100">Sub-topics</CardTitle>
              <CardDescription className="text-gray-400">
                Click on a sub-topic to view its details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topicNode.children.map((child: any, index: number) => {
                  const childTotals = calculateTotalTests(child);
                  const childPassRate =
                    childTotals.total > 0
                      ? (childTotals.passed / childTotals.total) * 100
                      : 0;

                  return (
                    <div
                      key={child.id}
                      className="flex items-center justify-between p-4 bg-gray-750 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
                      onClick={() =>
                        navigate({
                          to: `/ai-agents/${agentId}/topics/${child.id}`,
                        })
                      }
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          {childPassRate >= 80 ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : childPassRate >= 60 ? (
                            <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          <h3 className="text-sm font-medium text-gray-200">
                            {child.name}
                          </h3>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6 text-sm text-gray-400">
                        <span>Coverage: {child.testCoverage}%</span>
                        <span>Pass Rate: {childPassRate.toFixed(1)}%</span>
                        <span>
                          Tests: {childTotals.passed}/{childTotals.total}
                        </span>
                        {child.children && (
                          <span>{child.children.length} sub-topics</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AgentLayout>
  );
};
